import { matchFormatOptionFor } from './matchFormats';
import type { MatchBestOf, MatchFormat, MatchRules } from '../types/match';
import type { MatchEventOpLevel, MatchEventType } from '../types/matchEvent';

export interface MatchEventTypeOption {
  value: MatchEventType;
  label: string;
  helper: string;
  opLevel: MatchEventOpLevel;
  format: MatchFormat;
  recommendedBestOf: MatchBestOf;
  /** Prefilled round count before an attendance number is entered. */
  defaultRounds: number;
}

/**
 * The rules an event type starts with. Target score is not an event-level choice,
 * so it comes from the underlying format the same way a single match would.
 */
export function matchRulesForEventType(type: string | null | undefined): MatchRules {
  const option = matchEventTypeOptionFor(type);
  return {
    format: option.format,
    bestOf: option.recommendedBestOf,
    targetScore: matchFormatOptionFor(option.format).recommendedTargetScore,
  };
}

export const MATCH_EVENT_TYPE_OPTIONS: MatchEventTypeOption[] = [
  {
    value: 'nexus-night',
    label: 'Nexus Night',
    helper: 'Weekly casual night at your local store, store-chosen format',
    opLevel: 'casual',
    format: 'casual',
    recommendedBestOf: 3,
    defaultRounds: 3,
  },
  {
    value: 'summoner-skirmish',
    label: 'Summoner Skirmish',
    helper: 'Twice per set, 1v1 Constructed, top-8 promos and Regional Qualifier byes',
    opLevel: 'competitive',
    format: 'constructed',
    recommendedBestOf: 3,
    defaultRounds: 4,
  },
  {
    value: 'prerelease',
    label: 'Pre-Rift',
    helper: 'Sealed event the week before a new set releases',
    opLevel: 'casual',
    format: 'sealed',
    recommendedBestOf: 1,
    defaultRounds: 3,
  },
  {
    value: 'regional-qualifier',
    label: 'Regional Qualifier',
    helper: 'Open premier event, 1v1 Constructed, qualifies for Regional Championships',
    opLevel: 'competitive',
    format: 'constructed',
    recommendedBestOf: 3,
    defaultRounds: 6,
  },
  {
    value: 'regional-championship',
    label: 'Regional Championship',
    helper: 'Invite-only professional event with a Worlds invite on the line',
    opLevel: 'professional',
    format: 'constructed',
    recommendedBestOf: 3,
    defaultRounds: 7,
  },
  {
    value: 'worlds',
    label: 'Worlds',
    helper: 'The yearly world championship',
    opLevel: 'professional',
    format: 'constructed',
    recommendedBestOf: 3,
    defaultRounds: 8,
  },
  {
    value: 'custom',
    label: 'Casual night',
    helper: 'Any other night of games — testing, a pod with friends, a league',
    opLevel: 'casual',
    format: 'casual',
    recommendedBestOf: 1,
    defaultRounds: 3,
  },
];

export const MATCH_EVENT_TYPE_LABELS: Record<string, string> = MATCH_EVENT_TYPE_OPTIONS.reduce<Record<string, string>>(
  (labels, option) => {
    labels[option.value] = option.label;
    return labels;
  },
  {},
);

export const MATCH_EVENT_OP_LEVEL_LABELS: Record<MatchEventOpLevel, string> = {
  casual: 'Casual',
  competitive: 'Competitive',
  professional: 'Professional',
};

/** Default when nothing is chosen — the lowest-friction path for a night with friends. */
export const DEFAULT_MATCH_EVENT_TYPE: MatchEventType = 'nexus-night';

export const MIN_MATCH_EVENT_ROUNDS = 1;
export const MAX_MATCH_EVENT_ROUNDS = 16;

export function matchEventTypeOptionFor(type: string | null | undefined): MatchEventTypeOption {
  return MATCH_EVENT_TYPE_OPTIONS.find((option) => option.value === type) ?? MATCH_EVENT_TYPE_OPTIONS[0];
}

export function getMatchEventTypeLabel(type: string | null | undefined): string {
  if (!type) return MATCH_EVENT_TYPE_LABELS['nexus-night'];
  return MATCH_EVENT_TYPE_LABELS[type] ?? type;
}

/** Type label when it isn't already the event's name (the usual default). */
export function distinctMatchEventTypeLabel(
  type: string | null | undefined,
  name: string | null | undefined,
): string | null {
  const label = getMatchEventTypeLabel(type);
  if (!name?.trim()) return label;
  return name.trim().toLowerCase() === label.toLowerCase() ? null : label;
}

export function clampMatchEventRounds(rounds: number): number {
  if (!Number.isFinite(rounds)) return MIN_MATCH_EVENT_ROUNDS;
  return Math.min(MAX_MATCH_EVENT_ROUNDS, Math.max(MIN_MATCH_EVENT_ROUNDS, Math.round(rounds)));
}

/**
 * Swiss round count for a given attendance — the standard bracket every TCG uses,
 * so a 16-player Nexus Night suggests 4 rounds.
 */
export function suggestedRoundsForPlayers(playerCount: number | null | undefined): number {
  if (!playerCount || !Number.isFinite(playerCount) || playerCount < 2) return MIN_MATCH_EVENT_ROUNDS;
  const players = Math.round(playerCount);
  if (players <= 4) return 2;
  if (players <= 8) return 3;
  if (players <= 16) return 4;
  if (players <= 32) return 5;
  if (players <= 64) return 6;
  if (players <= 128) return 7;
  return 8;
}

/**
 * Prefill for event setup: Swiss from attendance when known, otherwise the
 * event-type default (e.g. Summoner Skirmish → 4).
 */
export function defaultRoundsForSetup(
  typeDefault: number,
  playerCount: number | null | undefined,
): number {
  if (playerCount == null) return clampMatchEventRounds(typeDefault);
  return suggestedRoundsForPlayers(playerCount);
}
